import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import ColorSizeWizard from '../components/ColorSizeWizard';
import { FormField, EmptyState } from '../components/ui';

export default function ColorSizeWizardPage() {
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [stockDetail, setStockDetail] = useState(null); // {id, color_id, size_id, product_code, ...}
  const [variants, setVariants] = useState([]);
  const [colors, setColors] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [wizardKey, setWizardKey] = useState(0); // force remount on completion to refresh

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/stocks/product-codes');
      if (r.data.success) setProducts(r.data.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const fetchLookups = useCallback(async () => {
    try {
      const [c, s] = await Promise.all([api.get('/colors'), api.get('/sizes')]);
      if (c.data.success) setColors(c.data.data || []);
      if (s.data.success) setSizes(s.data.data || []);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchProducts(); fetchLookups(); }, [fetchProducts, fetchLookups]);

  const fetchVariants = useCallback(async (productCode) => {
    if (!productCode) { setVariants([]); return; }
    try {
      const r = await api.get('/stocks', { params: { search: productCode, limit: 200 } });
      if (r.data.success) setVariants((r.data.data.items || []).filter(s => s.product_code === productCode));
    } catch (e) { console.error(e); }
  }, []);

  const pick = async (p) => {
    setSelected(p);
    setStockDetail(null);
    setVariants([]);
    if (!p.sample_stock_id) return;
    try {
      const r = await api.get('/stocks/' + p.sample_stock_id);
      if (r.data.success) setStockDetail(r.data.data);
    } catch (e) { console.error(e); }
    fetchVariants(p.product_code);
  };

  const filtered = !search ? products : products.filter(p =>
    (p.product_code || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.sample_name || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="master-detail-page">
      {/* SOL: ÜRÜN KODU LİSTESİ */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-secondary)', borderRadius: 12, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: 12, borderBottom: '1px solid var(--border-secondary)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <h3 style={{ margin: 0, fontSize: '0.95rem' }}>Ürün Kodları</h3>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>{products.length} ürün</span>
          </div>
          <FormField fullWidth>
            <input
              placeholder="Ürün kodu / ad ara..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </FormField>
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading && <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)' }}>Yükleniyor...</div>}
          {!loading && filtered.length === 0 && (
            <EmptyState
              compact
              title={products.length === 0 ? 'Ürün kodu yok' : 'Sonuç yok'}
              description={products.length === 0 ? 'Stok kartlarına ürün kodu girin.' : 'Eşleşen sonuç bulunamadı'}
            />
          )}
          {!loading && filtered.map(p => {
            const isActive = selected?.product_code === p.product_code;
            return (
              <button
                key={p.product_code}
                type="button"
                onClick={() => pick(p)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '10px 12px', border: 'none',
                  background: isActive ? 'var(--accent-glow)' : 'transparent',
                  borderLeft: isActive ? '3px solid var(--accent-primary)' : '3px solid transparent',
                  borderBottom: '1px solid var(--border-secondary)',
                  color: 'var(--text-primary)', cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                <div style={{ fontFamily: 'monospace', fontWeight: 700, color: isActive ? 'var(--accent-primary)' : 'var(--text-primary)', fontSize: '0.85rem' }}>
                  {p.product_code}
                </div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {p.sample_name}
                </div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', marginTop: 2 }}>
                  {p.variant_count} varyant
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* SAĞ: WIZARD */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-secondary)', borderRadius: 12, padding: 16, overflow: 'auto' }}>
        {msg && <div className={'detail-msg ' + msg.type} style={{ marginBottom: 12 }}>{msg.text}</div>}
        {!selected ? (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-tertiary)', fontSize: '0.9rem', flexDirection: 'column', gap: 8 }}>
            <span style={{ fontSize: '2rem' }}>🪄</span>
            <div><span className="desktop-only">Soldan</span><span className="mobile-only">Yukarıdan</span> bir ürün kodu seçin</div>
          </div>
        ) : !stockDetail ? (
          <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-tertiary)' }}>Yükleniyor...</div>
        ) : (
          <>
            <div style={{ marginBottom: 14, paddingBottom: 12, borderBottom: '1px solid var(--border-secondary)' }}>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>Ürün Kodu</div>
              <div style={{ fontFamily: 'monospace', fontSize: '1.1rem', fontWeight: 700, color: 'var(--accent-primary)' }}>{selected.product_code}</div>
              <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginTop: 4 }}>{selected.sample_name} — {selected.variant_count} varyant</div>
            </div>
            <ColorSizeWizard
              key={wizardKey + ':' + stockDetail.id}
              inline
              stock={stockDetail}
              colors={colors}
              sizes={sizes}
              onComplete={(res) => {
                const skipped = (res.skipped || []).length;
                setMsg({ type: 'success', text: `${res.count} yeni varyant oluşturuldu` + (skipped ? ` (${skipped} mevcut, atlandı)` : '') });
                setTimeout(() => setMsg(null), 5000);
                fetchProducts();
                fetchVariants(selected.product_code);
                setWizardKey(k => k + 1);
              }}
            />

            {/* Mevcut varyantlar listesi */}
            <div style={{ marginTop: 24, paddingTop: 16, borderTop: '2px solid var(--border-secondary)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <h4 style={{ margin: 0, fontSize: '0.9rem' }}>Bu Üründeki Varyantlar</h4>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>{variants.length} kayıt</span>
              </div>
              {variants.length === 0 ? (
                <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '0.82rem', background: 'var(--bg-input)', borderRadius: 8 }}>
                  Henüz varyant yok
                </div>
              ) : (
                <div style={{ border: '1px solid var(--border-secondary)', borderRadius: 8, overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                    <thead style={{ background: 'var(--bg-secondary)' }}>
                      <tr>
                        <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid var(--border-secondary)' }}>Stok Kodu</th>
                        <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid var(--border-secondary)' }}>Renk</th>
                        <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid var(--border-secondary)' }}>Beden</th>
                        <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid var(--border-secondary)' }}>Barkod</th>
                      </tr>
                    </thead>
                    <tbody>
                      {variants.map(v => (
                        <tr key={v.id} style={{ borderBottom: '1px solid var(--border-secondary)' }}>
                          <td style={{ padding: '7px 10px', fontFamily: 'monospace', color: 'var(--accent-primary)' }}>{v.code}</td>
                          <td style={{ padding: '7px 10px' }}>{v.color_name || '—'}</td>
                          <td style={{ padding: '7px 10px' }}>{v.size_name || '—'}</td>
                          <td style={{ padding: '7px 10px', fontFamily: 'monospace', fontSize: '0.76rem', color: 'var(--text-tertiary)' }}>{v.barcode || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div style={{ marginTop: 8, fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
                Detay düzenlemek için <strong>Stok İşlemleri → Stok / Ürünler</strong> sayfasına gidin ve stok kodunu arayın.
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
